import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LifebuoyIcon, PaperClipIcon, PaperAirplaneIcon, PlusIcon, XMarkIcon,
  LockClosedIcon, MagnifyingGlassIcon, ComputerDesktopIcon, KeyIcon,
  DocumentTextIcon, PhotoIcon, CheckCircleIcon, ArrowPathIcon, ChatBubbleLeftRightIcon,
  ArrowTrendingUpIcon, ArrowTrendingDownIcon, InboxStackIcon, ClockIcon, BoltIcon,
  QueueListIcon, ChartBarIcon, UserIcon, MapIcon,
} from '@heroicons/react/24/outline';
import Modal from '../components/Modal';
import { msalInstance } from '../auth/msalInstance';
import {
  useTicketMeta, useMyTickets, useTicketQueue, useTicketStats, useTicket,
  useRaiseTicket, useReplyToTicket, useUpdateTicket, useAttachToTicket, useAttachment,
  useAssignableUsers,
} from '../hooks/useItTickets';

// IT Support. One screen for everybody: your own tickets, plus the queue if you
// are on the IT team. Whether you are is decided by the SERVER (meta.isAgent) —
// the agent group is env-configured and the client has no business knowing it.

const STATUS_LABEL = {
  new: 'New', in_progress: 'In progress', waiting_on_user: 'Waiting on user',
  resolved: 'Resolved', closed: 'Closed',
};
// "Waiting on user" is how IT reads it; the person who logged the ticket sees
// "Waiting on you", since the ball is with them (23/10/2026).
const statusLabel = (t) => (t.status === 'waiting_on_user'
  && t.raised_by_entra_id && t.raised_by_entra_id === msalInstance.getActiveAccount()?.localAccountId
  ? 'Waiting on you' : STATUS_LABEL[t.status]);
const STATUS_TONE = {
  new: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  waiting_on_user: 'bg-purple-100 text-purple-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-gray-100 text-gray-600',
};
const PRIORITY_TONE = {
  urgent: 'bg-red-100 text-red-800', high: 'bg-orange-100 text-orange-800',
  normal: 'bg-gray-100 text-gray-600', low: 'bg-gray-100 text-gray-500',
};

const fmt = (v) => (v ? new Date(v).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');

// Compressed before upload for the same reason the delivery-note photo is: a
// raw phone screenshot is several megabytes and the cap is 6MB.
function readFile(file, maxPx = 1600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = () => {
      if (file.type === 'application/pdf') { resolve(reader.result); return; }
      const img = new Image();
      img.onerror = () => reject(new Error('That file is not an image or PDF.'));
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function Pill({ tone, children }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{children}</span>;
}

// The same three choices as the engineer app, with one swap: office staff live
// in the platform, not the mobile app, so 'Nano platform' takes the slot 'App
// Support' holds on FSM. Each carries its own wording -- one "What happened?"
// prompt is wrong for half of them, since somebody requesting access has not
// had anything happen.
const CATEGORY_CARDS = [
  {
    key: 'platform',
    label: 'Platform Support',
    Icon: ComputerDesktopIcon,
    summary: 'What is going wrong?',
    summaryHint: 'e.g. Inspection report will not open',
    detail: 'What were you doing when it happened?',
    detailHint: 'Which page you were on, what you expected, and what it did instead.',
  },
  {
    key: 'access_request',
    label: 'Access Request',
    Icon: KeyIcon,
    summary: 'What do you need access to?',
    summaryHint: 'e.g. SHEQ SharePoint site',
    detail: 'What do you need to do with it?',
    detailHint: 'Knowing why lets IT give you the right level rather than guessing.',
  },
  {
    key: 'general',
    label: 'IT Support',
    Icon: LifebuoyIcon,
    summary: 'What do you need?',
    summaryHint: 'e.g. Laptop will not charge',
    detail: 'Tell us a bit more',
    detailHint: 'Anything that helps IT sort it without having to come back and ask.',
  },
  // ODIN (07/10/2026): the same questions as IT Support, filed under its own
  // category so ODIN tickets can be found and reported on apart from the rest.
  {
    key: 'odin',
    label: 'ODIN Support',
    Icon: MapIcon,
    summary: 'What do you need?',
    summaryHint: 'e.g. Cannot see my jobs in ODIN',
    detail: 'Tell us a bit more',
    detailHint: 'Anything that helps IT sort it without having to come back and ask.',
  },
];

// Worded as the person experiences it, not as a support desk grades it.
const URGENCY = [
  { key: 'normal', label: 'Soon', off: 'border-emerald-300 bg-white text-emerald-800', on: 'border-emerald-600 bg-emerald-600 text-white' },
  { key: 'high', label: 'I am stuck', off: 'border-amber-300 bg-white text-amber-800', on: 'border-amber-500 bg-amber-500 text-white' },
  { key: 'urgent', label: 'Cannot work', off: 'border-red-300 bg-white text-red-700', on: 'border-red-600 bg-red-600 text-white' },
];

function RaiseForm({ onClose }) {
  const raise = useRaiseTicket();
  const attach = useAttachToTicket();
  const [cat, setCat] = useState(null);
  // priority starts EMPTY on purpose. A preselected default is our guess wearing
  // the raiser's name, and would quietly become the answer on every ticket
  // nobody thought about.
  const [form, setForm] = useState({ subject: '', body: '', priority: '' });
  const [shots, setShots] = useState([]);
  const [err, setErr] = useState('');
  const [stage, setStage] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // Captured rather than asked for: nobody should have to know their own browser
  // version to report a bug.
  const context = `${window.location.pathname} · ${navigator.userAgent.slice(0, 180)}`;
  const chosen = CATEGORY_CARDS.find((c) => c.key === cat);

  async function addShot(file) {
    setErr('');
    try {
      // Resolved before the state update: an updater callback is not async.
      const next = await readFile(file);
      setShots((l) => [...l, next]);
    } catch (e) { setErr(e.message); }
  }

  async function submit() {
    setErr('');
    if (!form.subject.trim()) { setErr('Give it a one-line summary so IT can see what it is.'); return; }
    if (!form.body.trim()) { setErr('Add a bit of detail — it saves IT coming back to ask.'); return; }
    if (!form.priority) { setErr('Let IT know how urgent it is.'); return; }
    try {
      setStage('Sending…');
      const t = await raise.mutateAsync({ ...form, category: cat, context });
      for (let i = 0; i < shots.length; i += 1) {
        setStage(`Attaching file ${i + 1} of ${shots.length}…`);
        await attach.mutateAsync({ id: t.id, file: shots[i], name: `attachment-${i + 1}` });
      }
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not send that. Try again.');
      setStage('');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 py-10">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">What do you need help with?</h2>
            <p className="text-sm text-gray-500">Pick the closest one — IT can move it if it lands in the wrong place.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Three things you can see beat a dropdown of three things you cannot. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {CATEGORY_CARDS.map(({ key, label, Icon }) => (
            <button key={key} type="button" onClick={() => setCat(key)}
              className={'flex items-center gap-3 rounded-md border p-4 text-left transition ' + (cat === key
                ? 'border-brand bg-brand-light ring-1 ring-brand'
                : 'border-gray-300 bg-white hover:border-brand hover:bg-brand-light/40')}>
              <span className={'flex h-10 w-10 shrink-0 items-center justify-center rounded-md ' + (cat === key ? 'bg-brand' : 'bg-gray-100')}>
                <Icon className={'h-5 w-5 ' + (cat === key ? 'text-white' : 'text-gray-500')} />
              </span>
              <span className="text-sm font-semibold text-gray-900">{label}</span>
            </button>
          ))}
        </div>

        {/* Nothing else appears until there is something to say it about. */}
        {chosen ? (
          <>
            <label className="mt-6 block">
              <span className="mb-1 block text-sm font-semibold text-gray-800">{chosen.summary}</span>
              <input value={form.subject} onChange={set('subject')} maxLength={200}
                placeholder={chosen.summaryHint}
                className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm" />
            </label>

            <label className="mt-4 block">
              <span className="mb-1 block text-sm font-semibold text-gray-800">{chosen.detail}</span>
              <span className="mb-1 block text-xs text-gray-500">{chosen.detailHint}</span>
              <textarea value={form.body} onChange={set('body')} rows={5}
                className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm" />
            </label>

            <div className="mt-4">
              <span className="mb-1 block text-sm font-semibold text-gray-800">
                Screenshot <span className="font-normal text-gray-500">— optional, but it saves a lot of back and forth</span>
              </span>
              {shots.length ? (
                <div className="mb-2 grid grid-cols-4 gap-2">
                  {shots.map((src, i) => (
                    /* eslint-disable-next-line react/no-array-index-key */
                    <div key={i} className="relative">
                      {/^data:application\/pdf/.test(src) ? (
                        <div className="flex h-24 w-full items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-xs font-semibold text-gray-500">
                          PDF
                        </div>
                      ) : (
                        <img src={src} alt={`Attachment ${i + 1}`} className="h-24 w-full rounded-md border border-gray-200 object-cover" />
                      )}
                      <button type="button" onClick={() => setShots((l) => l.filter((_, j) => j !== i))}
                        aria-label={`Remove attachment ${i + 1}`}
                        className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900/80 text-xs font-bold text-white">
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-gray-300 py-4 text-sm font-semibold text-gray-600 hover:border-brand hover:text-brand">
                <PaperClipIcon className="h-5 w-5 text-brand" />
                {shots.length ? 'Add another' : 'Add a screenshot or PDF'}
                <input type="file" accept="image/*,application/pdf" className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) addShot(e.target.files[0]);
                    // Cleared so the same file can be chosen twice running.
                    e.target.value = '';
                  }} />
              </label>
            </div>

            <div className="mt-4">
              <span className="mb-1 block text-sm font-semibold text-gray-800">How urgent is it?</span>
              <div className="grid max-w-md grid-cols-3 gap-2">
                {URGENCY.map((u) => (
                  <button key={u.key} type="button" onClick={() => setForm((f) => ({ ...f, priority: u.key }))}
                    className={'rounded-md border px-2 py-2 text-sm font-semibold ' + (form.priority === u.key ? u.on : u.off)}>
                    {u.label}
                  </button>
                ))}
              </div>
            </div>

            {err ? (
              <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>
            ) : null}

            <div className="mt-6 flex items-center justify-end gap-2">
              <button type="button" onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">
                Cancel
              </button>
              <button type="button" disabled={Boolean(stage)} onClick={submit}
                className="rounded-md bg-brand px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {stage || 'Send to the IT department'}
              </button>
            </div>
            <p className="mt-2 text-right text-xs text-gray-500">
              You will get a reply on this ticket — you do not need to chase it.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}

// The attachment arrives as a data: URL. Browsers refuse to open a data: URL as
// a page of its own, so "Open PDF" linking straight to it gave a blank tab
// (TKT-00022). A blob: URL made from the same bytes opens normally.
function dataUrlToBlobUrl(dataUrl) {
  const [head, b64] = dataUrl.split(',');
  const mime = (head.match(/^data:([^;]+)/) || [])[1] || 'application/octet-stream';
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  return URL.createObjectURL(new Blob([arr], { type: mime }));
}

const fmtSize = (b) => {
  const n = Number(b) || 0;
  if (!n) return '';
  return n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
};

function Attachment({ attachment }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useAttachment(attachment.id, open);
  const isPdf = attachment.mime_type === 'application/pdf';
  const blobUrl = useMemo(() => (data?.file ? dataUrlToBlobUrl(data.file) : null), [data?.file]);
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);
  // Emailed attachments can arrive named "attachment-1" with no extension.
  const name = attachment.original_name || 'Attachment';
  const downloadName = isPdf && !/\.pdf$/i.test(name) ? `${name}.pdf` : name;
  const Icon = isPdf ? DocumentTextIcon : PhotoIcon;
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="inline-flex max-w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-left text-xs hover:border-brand">
        <Icon className="h-4 w-4 shrink-0 text-brand" />
        <span className="truncate font-semibold text-gray-800">{name}</span>
        {attachment.size_bytes ? <span className="shrink-0 text-gray-400">{fmtSize(attachment.size_bytes)}</span> : null}
        <span className="shrink-0 font-semibold text-brand">{open ? 'Hide' : 'View'}</span>
      </button>
      {open && (
        <div className="mt-2">
          {isLoading && <p className="text-xs text-gray-400">Loading…</p>}
          {blobUrl && (isPdf
            ? (
              <span className="inline-flex gap-3">
                <a href={blobUrl} target="_blank" rel="noreferrer" className="text-xs font-semibold text-brand underline">Open PDF</a>
                <a href={blobUrl} download={downloadName} className="text-xs font-semibold text-brand underline">Download</a>
              </span>
            )
            : <img src={blobUrl} alt={name}
                className="max-h-96 rounded-md border border-gray-200" />)}
        </div>
      )}
    </div>
  );
}

// ---- Small shared bits ------------------------------------------------------

const n = (v) => Number(v) || 0;

// "3h 40m", "45 min", "2.5 days" -- a helpdesk thinks in hours, not decimals.
function fmtDuration(h) {
  if (h == null) return '—';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) {
    const whole = Math.floor(h);
    const m = Math.round((h - whole) * 60);
    return m ? `${whole}h ${m}m` : `${whole}h`;
  }
  return `${(h / 24).toFixed(1)} days`;
}

function ago(v) {
  if (!v) return '';
  const h = (Date.now() - new Date(v).getTime()) / 3600000;
  if (h < 1 / 60) return 'just now';
  if (h < 1) return `${Math.round(h * 60)}m ago`;
  if (h < 24) return `${Math.round(h)}h ago`;
  const d = Math.round(h / 24);
  return d < 14 ? `${d}d ago` : new Date(v).toLocaleDateString('en-GB');
}

const initials = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

function Avatar({ name, tone = 'bg-gray-200 text-gray-700', size = 'h-8 w-8 text-xs' }) {
  return (
    <span title={name || ''} className={`inline-flex shrink-0 items-center justify-center rounded-full font-bold ${size} ${tone}`}>
      {initials(name)}
    </span>
  );
}

const PRIORITY_BAR = { urgent: 'bg-red-500', high: 'bg-orange-400', normal: 'bg-gray-200', low: 'bg-gray-100' };
const PRIORITY_LABEL = { urgent: 'Urgent', high: 'High', normal: 'Normal', low: 'Low' };
const STATUS_DOT = {
  new: 'bg-blue-500', in_progress: 'bg-amber-500', waiting_on_user: 'bg-purple-500',
  resolved: 'bg-emerald-500', closed: 'bg-gray-400',
};

// ---- Ticket detail ----------------------------------------------------------

const EVENT_TEXT = {
  status: (d) => {
    const [a, b] = String(d || '').split(' -> ');
    return `changed the status from ${STATUS_LABEL[a] || a} to ${STATUS_LABEL[b] || b}`;
  },
  priority: (d) => `changed the priority: ${String(d || '').replace(' -> ', ' → ')}`,
  category: (d) => `moved it: ${String(d || '').replace(' -> ', ' → ')}`,
  assigned: (d) => (d && d !== 'unassigned' ? `assigned it to ${d}` : 'unassigned it'),
};

function Fact({ label, children }) {
  return (
    <div className="py-2">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-800">{children || '—'}</dd>
    </div>
  );
}

const selectCls = 'mt-0.5 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm';

function TicketDetail({ id, isAgent, meta, onClose }) {
  const { data: t, isLoading } = useTicket(id);
  const reply = useReplyToTicket();
  const update = useUpdateTicket();
  const attach = useAttachToTicket();
  const { data: assignable } = useAssignableUsers(isAgent);
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [err, setErr] = useState('');
  const endRef = useRef(null);

  // Keep the newest message in view. A long ticket otherwise opens at the
  // original report, which is the least useful part by the time IT is working it.
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [t?.messages?.length]);

  // Attachments are stored against the ticket and optionally a message, so group
  // them once rather than filtering inside the render loop.
  const attachments = useMemo(() => {
    const byMessage = new Map();
    (t?.attachments || []).forEach((a) => {
      const k = a.message_id || 'ticket';
      byMessage.set(k, [...(byMessage.get(k) || []), a]);
    });
    return byMessage;
  }, [t]);

  // One timeline: the conversation, with IT's status/assignment changes threaded
  // in between (agents only -- the server sends no events to anyone else). The
  // 'raised' event is the first message, so it is not repeated.
  const timeline = useMemo(() => {
    if (!t) return [];
    const items = [
      ...t.messages.map((m) => ({ kind: 'message', at: m.created_at, m })),
      ...(t.events || []).filter((e) => e.event !== 'raised' && EVENT_TEXT[e.event])
        .map((e, i) => ({ kind: 'event', at: e.created_at, e, i })),
    ];
    return items.sort((a, b) => new Date(a.at) - new Date(b.at));
  }, [t]);

  async function addFile(file) {
    setErr('');
    try { attach.mutate({ id, file: await readFile(file), name: file.name }); }
    catch (e) { setErr(e.message); }
  }

  const assignSelf = () => update.mutate({ id, assigned_to: 'me', assigned_name: 'me' });
  const setStatus = (status) => update.mutate({ id, status });
  const send = () => reply.mutate({ id, body, isInternal: internal },
    { onSuccess: () => { setBody(''); setInternal(false); } });

  if (isLoading || !t) {
    return (
      <Modal title="Loading ticket…" onClose={onClose} width="max-w-5xl">
        <p className="py-6 text-sm text-gray-400">Loading…</p>
      </Modal>
    );
  }

  const isOpen = ['new', 'in_progress', 'waiting_on_user'].includes(t.status);
  const doneAt = t.resolved_at || t.closed_at;
  const responseH = t.first_response_at ? (new Date(t.first_response_at) - new Date(t.created_at)) / 3600000 : null;
  const resolveH = doneAt ? (new Date(doneAt) - new Date(t.created_at)) / 3600000 : null;

  const composer = (
    <div>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && body.trim()) send(); }}
        placeholder={internal ? 'Internal note — the person who raised this will not see it' : 'Write a reply…'}
        className={'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 ' + (internal ? 'border-amber-300 bg-amber-50' : 'border-gray-300')} />
      {err ? <p className="mt-2 text-sm text-red-700">{err}</p> : null}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-4">
          <label className="inline-flex cursor-pointer items-center gap-1 text-sm text-gray-600 hover:text-brand">
            <PaperClipIcon className="h-4 w-4" /> Attach
            <input type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => { if (e.target.files && e.target.files[0]) addFile(e.target.files[0]); e.target.value = ''; }} />
          </label>
          {isAgent && (
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-gray-600">
              <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
              <LockClosedIcon className="h-3.5 w-3.5" /> Internal note
            </label>
          )}
          <span className="hidden text-xs text-gray-400 sm:inline">Ctrl + Enter to send</span>
        </div>
        <button type="button" disabled={reply.isPending || !body.trim()} onClick={send}
          className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          <PaperAirplaneIcon className="h-4 w-4" />
          {reply.isPending ? 'Sending…' : internal ? 'Add note' : 'Send reply'}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      width="max-w-5xl"
      onClose={onClose}
      title={t.subject}
      subtitle={`${t.reference} · ${meta?.categories?.[t.category] || t.category} · raised by ${t.raised_by_name || 'unknown'} ${ago(t.created_at)}`}
      footer={composer}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Pill tone={STATUS_TONE[t.status]}>{statusLabel(t)}</Pill>
        <Pill tone={PRIORITY_TONE[t.priority]}>{PRIORITY_LABEL[t.priority] || t.priority} priority</Pill>
        {!t.first_response_at && ['new', 'in_progress'].includes(t.status) ? (
          <Pill tone="bg-rose-100 text-rose-700">Awaiting first response</Pill>
        ) : null}
        {isAgent && isOpen ? (
          <span className="ml-auto flex flex-wrap gap-2">
            {!t.assigned_to && (
              <button type="button" onClick={assignSelf}
                className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:border-brand hover:text-brand">
                Take it
              </button>
            )}
            {t.status !== 'waiting_on_user' && (
              <button type="button" onClick={() => setStatus('waiting_on_user')}
                className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:border-purple-400 hover:text-purple-700">
                Waiting on user
              </button>
            )}
            {/* Resolve closes the ticket (06/10/2026); the raiser is emailed and
                can still reply to reopen it. */}
            <button type="button" onClick={() => setStatus('closed')}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700">
              <CheckCircleIcon className="h-4 w-4" /> Resolve
            </button>
          </span>
        ) : null}
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_17rem]">
        {/* Conversation */}
        <div className="min-w-0">
          <ol className="space-y-4">
            {timeline.map((item) => {
              if (item.kind === 'event') {
                const e = item.e;
                return (
                  <li key={`e${item.i}`} className="flex items-center gap-2 pl-11 text-xs text-gray-500">
                    <ArrowPathIcon className="h-3.5 w-3.5 text-gray-400" />
                    <span><span className="font-semibold text-gray-700">{e.by_name || 'IT'}</span> {EVENT_TEXT[e.event](e.detail)}</span>
                    <span className="text-gray-400">· {fmt(e.created_at)}</span>
                  </li>
                );
              }
              const m = item.m;
              const fromUser = m.sender_type === 'user';
              return (
                <li key={m.id} className="flex gap-3">
                  <Avatar name={m.sender_name}
                    tone={m.is_internal ? 'bg-amber-100 text-amber-800' : fromUser ? 'bg-gray-200 text-gray-700' : 'bg-brand text-white'} />
                  <div className={'min-w-0 flex-1 rounded-lg border px-4 py-3 ' + (m.is_internal
                    ? 'border-amber-200 bg-amber-50'
                    : fromUser ? 'border-gray-200 bg-white' : 'border-brand/20 bg-brand-light/60')}>
                    <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-xs">
                      <span className="text-sm font-semibold text-gray-900">{m.sender_name || 'Unknown'}</span>
                      <span className={fromUser ? 'text-gray-400' : 'font-semibold text-brand'}>{fromUser ? 'Requester' : 'IT'}</span>
                      {m.is_internal ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                          <LockClosedIcon className="h-3 w-3" /> Internal note
                        </span>
                      ) : null}
                      <span className="ml-auto text-gray-400">{fmt(m.created_at)}</span>
                    </div>
                    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">{m.body}</p>
                    {(attachments.get(m.id) || []).map((a) => <Attachment key={a.id} attachment={a} />)}
                  </div>
                </li>
              );
            })}
          </ol>
          {(attachments.get('ticket') || []).length ? (
            <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Attached to the ticket</div>
              {(attachments.get('ticket') || []).map((a) => <Attachment key={a.id} attachment={a} />)}
            </div>
          ) : null}
          <div ref={endRef} />
        </div>

        {/* Details */}
        <aside className="h-fit rounded-lg border border-gray-200 bg-white px-4 py-2">
          <dl className="divide-y divide-gray-100">
            {isAgent ? (
              <>
                <Fact label="Status">
                  <select value={t.status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
                    {(meta?.statuses || []).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </Fact>
                <Fact label="Priority">
                  <select value={t.priority} onChange={(e) => update.mutate({ id, priority: e.target.value })} className={selectCls}>
                    {(meta?.priorities || []).map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p] || p}</option>)}
                  </select>
                </Fact>
                <Fact label="Category">
                  <select value={t.category} onChange={(e) => update.mutate({ id, category: e.target.value })} className={selectCls}>
                    {Object.entries(meta?.categories || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </Fact>
                <Fact label="Assigned to">
                  <select
                    value={t.assigned_to || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) return update.mutate({ id, assigned_to: null, assigned_name: null });
                      if (v === '__me') return assignSelf();
                      const p = (assignable || []).find((x) => x.entraId === v);
                      return update.mutate({ id, assigned_to: v, assigned_name: p ? p.name : (t.assigned_name || null) });
                    }}
                    className={selectCls}
                  >
                    <option value="">Unassigned</option>
                    <option value="__me">Assign to me</option>
                    {/* Keep the current assignee selectable even if they aren't in
                        the picker list (e.g. a self-assigned agent in neither group). */}
                    {t.assigned_to && !(assignable || []).some((x) => x.entraId === t.assigned_to) && (
                      <option value={t.assigned_to}>{t.assigned_name || 'Currently assigned'}</option>
                    )}
                    {(assignable || []).map((p) => (
                      <option key={p.entraId} value={p.entraId}>{p.name}</option>
                    ))}
                  </select>
                </Fact>
              </>
            ) : (
              <>
                <Fact label="Category">{meta?.categories?.[t.category]}</Fact>
                <Fact label="With">{t.assigned_name || 'Not picked up yet'}</Fact>
              </>
            )}
            <Fact label="Raised by">
              <span className="flex items-center gap-2">
                <Avatar name={t.raised_by_name} size="h-6 w-6 text-[10px]" />
                <span className="min-w-0">
                  <span className="block truncate">{t.raised_by_name || '—'}</span>
                  {t.raised_by_email ? (
                    <a href={`mailto:${t.raised_by_email}`} className="block truncate text-xs text-brand hover:underline">{t.raised_by_email}</a>
                  ) : null}
                </span>
              </span>
            </Fact>
            <Fact label="Raised">{fmt(t.created_at)}</Fact>
            <Fact label="First response">
              {t.first_response_at ? (
                <>{fmt(t.first_response_at)} <span className="text-xs text-gray-500">({fmtDuration(responseH)})</span></>
              ) : 'Not yet'}
            </Fact>
            {doneAt ? (
              <Fact label={t.resolved_at ? 'Resolved' : 'Closed'}>
                {fmt(doneAt)} <span className="text-xs text-gray-500">({fmtDuration(resolveH)})</span>
              </Fact>
            ) : (
              <Fact label="Open for">{fmtDuration((Date.now() - new Date(t.created_at)) / 3600000)}</Fact>
            )}
            {isAgent && t.context ? (
              <Fact label="Raised from">
                <span className="block break-all font-mono text-[11px] leading-snug text-gray-500">{t.context}</span>
              </Fact>
            ) : null}
          </dl>
        </aside>
      </div>
    </Modal>
  );
}

// ---- Lists ------------------------------------------------------------------

function TicketRow({ ticket, meta, onOpen }) {
  // Waiting-on-user already says the ball is with the requester, so the flag
  // is only for tickets IT still owes a first word on.
  const unanswered = !ticket.first_response_at && ['new', 'in_progress'].includes(ticket.status);
  // The requester has followed up and IT has not answered since: a reply is waiting.
  const replied = Number(ticket.message_count) > 1 && ticket.last_sender_type === 'user'
    && ['new', 'in_progress', 'waiting_on_user'].includes(ticket.status);
  return (
    <li>
      <button type="button" onClick={() => onOpen(ticket.id)}
        className="group flex w-full items-stretch gap-3 text-left hover:bg-brand-light/30">
        <span className={'w-1 shrink-0 ' + (PRIORITY_BAR[ticket.priority] || 'bg-gray-200')} aria-hidden />
        <span className="flex min-w-0 flex-1 items-center gap-4 py-3 pr-4">
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs font-semibold text-gray-500">{ticket.reference}</span>
              <Pill tone={STATUS_TONE[ticket.status]}>{statusLabel(ticket)}</Pill>
              {ticket.priority === 'urgent' || ticket.priority === 'high'
                ? <Pill tone={PRIORITY_TONE[ticket.priority]}>{PRIORITY_LABEL[ticket.priority]}</Pill> : null}
              {unanswered ? <Pill tone="bg-rose-50 text-rose-700">No reply yet</Pill> : null}
              {replied ? <Pill tone="bg-rose-100 text-rose-700">Replied {ago(ticket.last_message_at)}</Pill> : null}
            </span>
            <span className="mt-1 block truncate text-[15px] font-semibold text-gray-900 group-hover:text-brand">{ticket.subject}</span>
            <span className="mt-0.5 block truncate text-xs text-gray-500">
              {meta?.categories?.[ticket.category]}
              <span className="mx-1.5 text-gray-300">•</span>
              {ticket.raised_by_name}
              <span className="mx-1.5 text-gray-300">•</span>
              raised {fmt(ticket.created_at)}
            </span>
          </span>
          <span className="hidden shrink-0 items-center gap-3 text-xs text-gray-400 sm:flex">
            {n(ticket.message_count) > 0 && (
              <span className="inline-flex items-center gap-1" title="Messages">
                <ChatBubbleLeftRightIcon className="h-4 w-4" />{n(ticket.message_count)}
              </span>
            )}
            {n(ticket.attachment_count) > 0 && (
              <span className="inline-flex items-center gap-1" title="Attachments">
                <PaperClipIcon className="h-4 w-4" />{n(ticket.attachment_count)}
              </span>
            )}
          </span>
          <span className="hidden w-24 shrink-0 text-right text-xs text-gray-500 md:block" title={`Updated ${fmt(ticket.updated_at)}`}>
            {ago(ticket.updated_at)}
          </span>
          <span className="hidden shrink-0 md:block">
            {ticket.assigned_name
              ? <Avatar name={ticket.assigned_name} tone="bg-brand-light text-brand" />
              : <span title="Unassigned" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-300">?</span>}
          </span>
        </span>
      </button>
    </li>
  );
}

function TicketList({ tickets, meta, onOpen, empty }) {
  if (!tickets) return <p className="px-4 py-8 text-sm text-gray-400">Loading…</p>;
  if (!tickets.length) {
    return <p className="px-4 py-10 text-center text-sm text-gray-400">{empty}</p>;
  }
  return <ul className="divide-y divide-gray-100">{tickets.map((t) => (
    <TicketRow key={t.id} ticket={t} meta={meta} onOpen={onOpen} />
  ))}</ul>;
}

// ---- Metrics ----------------------------------------------------------------

function Delta({ now, prev, goodWhenUp }) {
  const d = n(now) - n(prev);
  if (!d) return <span className="text-xs text-gray-400">same as last week</span>;
  const good = goodWhenUp ? d > 0 : d < 0;
  const Icon = d > 0 ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  return (
    <span className={'inline-flex items-center gap-1 text-xs font-semibold ' + (good ? 'text-emerald-600' : 'text-rose-600')}>
      <Icon className="h-3.5 w-3.5" />{d > 0 ? '+' : ''}{d} vs last week
    </span>
  );
}

function Sparkline({ values, tone = 'stroke-brand' }) {
  if (!values || values.length < 2) return null;
  const max = Math.max(1, ...values);
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * 100},${28 - (v / max) * 26}`).join(' ');
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="mt-2 h-8 w-full">
      <polyline points={pts} fill="none" className={tone} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}

function Kpi({ label, value, sub, accent = 'text-gray-900', icon: Icon, spark, sparkTone }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
        {Icon ? <Icon className="h-4 w-4 text-gray-300" /> : null}
      </div>
      <div className={'mt-1 text-2xl font-bold tabular-nums ' + accent}>{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-gray-500">{sub}</div> : null}
      {spark ? <Sparkline values={spark} tone={sparkTone} /> : null}
    </div>
  );
}

function KpiStrip({ s }) {
  const open = n(s?.new_count) + n(s?.in_progress) + n(s?.waiting);
  const series = s?.series || [];
  const last14 = series.slice(-14);
  const t = s?.times || {};
  const w = s?.week || {};
  return (
    <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
      <Kpi label="Open now" value={open} icon={InboxStackIcon}
        sub={s?.oldest_open ? `Oldest ${s.oldest_open.reference}, ${s.oldest_open.age_days}d` : 'Nothing waiting'}
        spark={last14.map((d) => d.backlog)} />
      <Kpi label="Raised this week" value={n(w.opened)} icon={PlusIcon}
        sub={<Delta now={w.opened} prev={w.opened_prev} />}
        spark={last14.map((d) => d.opened)} sparkTone="stroke-blue-500" />
      <Kpi label="Closed this week" value={n(w.closed)} accent="text-emerald-700" icon={CheckCircleIcon}
        sub={<Delta now={w.closed} prev={w.closed_prev} goodWhenUp />}
        spark={last14.map((d) => d.closed)} sparkTone="stroke-emerald-500" />
      <Kpi label="First response" value={fmtDuration(t.first_response_median_h)} icon={ClockIcon}
        sub={t.responded_within_4h_pct != null ? `${t.responded_within_4h_pct}% answered within 4h` : 'median, last 30 days'} />
      <Kpi label="Time to resolve" value={fmtDuration(t.resolution_median_h)} icon={BoltIcon}
        sub={t.resolved_within_24h_pct != null ? `${t.resolved_within_24h_pct}% sorted within a day` : 'median, last 30 days'} />
    </div>
  );
}

// Status pills double as the queue filter, with live counts on each.
function StatusPills({ s, value, onChange }) {
  const open = n(s?.new_count) + n(s?.in_progress) + n(s?.waiting);
  const pills = [
    ['open', 'Open', open, 'bg-brand'],
    ['needs_reply', 'Needs reply', s?.needs_reply, 'bg-rose-500'],
    ['new', 'New', s?.new_count, STATUS_DOT.new],
    ['in_progress', 'In progress', s?.in_progress, STATUS_DOT.in_progress],
    ['waiting_on_user', 'Waiting on user', s?.waiting, STATUS_DOT.waiting_on_user],
    ['unassigned', 'Unassigned', s?.unassigned, 'bg-rose-500'],
    ['resolved', 'Resolved', s?.resolved, STATUS_DOT.resolved],
    ['closed', 'Closed', s?.closed, STATUS_DOT.closed],
    ['all', 'All', s?.total, 'bg-gray-700'],
  ];
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {pills.map(([k, label, count, dot]) => {
        const on = value === k;
        return (
          <button key={k} type="button" onClick={() => onChange(k)}
            className={'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition '
              + (on ? 'border-brand bg-brand text-white shadow-sm' : 'border-gray-200 bg-white text-gray-700 hover:border-brand/50')}>
            <span className={'h-2 w-2 rounded-full ' + (on ? 'bg-white' : dot)} />
            {label}
            <span className={'rounded-full px-1.5 text-xs tabular-nums ' + (on ? 'bg-white/20' : 'bg-gray-100 text-gray-600')}>{n(count)}</span>
          </button>
        );
      })}
    </div>
  );
}

function Card({ title, sub, children, className = '' }) {
  return (
    <section className={'rounded-xl border border-gray-200 bg-white p-4 shadow-sm ' + className}>
      <h3 className="text-sm font-bold text-gray-900">{title}</h3>
      {sub ? <p className="text-xs text-gray-500">{sub}</p> : null}
      <div className="mt-3">{children}</div>
    </section>
  );
}

const dm = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

// Raised vs closed per day as paired bars, with the open backlog as a line.
function FlowChart({ series }) {
  const [hover, setHover] = useState(null);
  if (!series?.length) return null;
  const W = 600; const H = 180; const pad = { l: 26, r: 8, t: 8, b: 22 };
  const max = Math.max(2, ...series.map((d) => Math.max(d.opened, d.closed, d.backlog)));
  const iw = W - pad.l - pad.r; const ih = H - pad.t - pad.b;
  const step = iw / series.length; const bw = Math.max(2, step * 0.36);
  const y = (v) => pad.t + ih - (v / max) * ih;
  const line = series.map((d, i) => `${pad.l + step * i + step / 2},${y(d.backlog)}`).join(' ');
  const ticks = [0, Math.round(max / 2), max];
  const h = hover != null ? series[hover] : null;
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-48 w-full" onMouseLeave={() => setHover(null)}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} className="stroke-gray-100" />
            <text x={pad.l - 4} y={y(v) + 3} textAnchor="end" className="fill-gray-400 text-[9px]">{v}</text>
          </g>
        ))}
        {series.map((d, i) => {
          const x = pad.l + step * i;
          return (
            <g key={d.date} onMouseEnter={() => setHover(i)}>
              <rect x={x} y={pad.t} width={step} height={ih} className={hover === i ? 'fill-gray-50' : 'fill-transparent'} />
              <rect x={x + step / 2 - bw} y={y(d.opened)} width={bw} height={pad.t + ih - y(d.opened)} rx="1.5" className="fill-blue-400" />
              <rect x={x + step / 2} y={y(d.closed)} width={bw} height={pad.t + ih - y(d.closed)} rx="1.5" className="fill-emerald-400" />
              {(i % 5 === 0 || i === series.length - 1) && (
                <text x={x + step / 2} y={H - 6} textAnchor="middle" className="fill-gray-400 text-[9px]">{dm(d.date)}</text>
              )}
            </g>
          );
        })}
        <polyline points={line} fill="none" className="stroke-brand" strokeWidth="2" strokeLinejoin="round" pointerEvents="none" />
      </svg>
      <div className="mt-1 flex flex-wrap items-center gap-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-400" />Raised</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-400" />Closed</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-brand" />Open at end of day</span>
        {h ? (
          <span className="ml-auto font-semibold text-gray-700">
            {new Date(h.date).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}:
            {' '}{h.opened} raised, {h.closed} closed, {h.backlog} open
          </span>
        ) : null}
      </div>
    </div>
  );
}

function Bars({ rows, tone = 'bg-brand', openTone = 'bg-brand/40' }) {
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => (
        <li key={r.label}>
          <div className="mb-1 flex items-baseline justify-between text-sm">
            <span className="font-medium text-gray-800">{r.label}</span>
            <span className="tabular-nums text-gray-500">
              {r.total}{r.open != null && r.open > 0 ? <span className="ml-1 text-xs text-gray-400">({r.open} open)</span> : null}
            </span>
          </div>
          <div className="relative h-2 overflow-hidden rounded-full bg-gray-100">
            <div className={'absolute inset-y-0 left-0 rounded-full ' + tone} style={{ width: `${(r.total / max) * 100}%` }} />
            {r.open ? <div className={'absolute inset-y-0 left-0 rounded-full ' + openTone} style={{ width: `${(r.open / max) * 100}%` }} /> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 15 }, (_, i) => i + 6); // 06:00 - 20:00

// When the tickets arrive, weekday by hour. Anything outside 06-20 folds into
// the nearest edge column so nothing silently disappears.
function Heatmap({ heat }) {
  if (!heat) return null;
  const grid = heat.map((row) => HOURS.map((h, i) => {
    if (i === 0) return row.slice(0, h + 1).reduce((a, b) => a + b, 0);
    if (i === HOURS.length - 1) return row.slice(h).reduce((a, b) => a + b, 0);
    return row[h];
  }));
  const max = Math.max(1, ...grid.flat());
  const shade = (v) => (!v ? 'bg-gray-50' : v / max > 0.75 ? 'bg-brand' : v / max > 0.5 ? 'bg-brand/70' : v / max > 0.25 ? 'bg-brand/45' : 'bg-brand/20');
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate" style={{ borderSpacing: 3 }}>
        <thead>
          <tr>
            <th />
            {HOURS.map((h) => (
              <th key={h} className="text-[10px] font-normal text-gray-400">{h % 2 === 0 ? String(h).padStart(2, '0') : ''}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((row, d) => (
            <tr key={DAYS[d]}>
              <td className="pr-1 text-right text-[11px] text-gray-500">{DAYS[d]}</td>
              {row.map((v, i) => (
                <td key={HOURS[i]} title={`${DAYS[d]} ${String(HOURS[i]).padStart(2, '0')}:00 — ${v} ticket${v === 1 ? '' : 's'}`}
                  className={'h-5 min-w-[14px] rounded-sm ' + shade(v)} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Insights({ s }) {
  if (!s?.series) return <p className="py-8 text-sm text-gray-400">Loading…</p>;
  const t = s.times || {};
  const priorities = (s.by_priority || []).filter((p) => p.total || p.open).reverse()
    .map((p) => ({ label: PRIORITY_LABEL[p.key] || p.key, total: p.total, open: p.open }));
  const priorityTone = { Urgent: 'bg-red-500', High: 'bg-orange-400', Normal: 'bg-gray-400', Low: 'bg-gray-300' };
  return (
    <div className="space-y-4">
      <Card title="Ticket flow" sub={`Raised and closed each day, last ${s.window_days} days — ${s.created_window} raised, ${s.closed_window} closed`}>
        <FlowChart series={s.series} />
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="By category" sub={`Last ${s.window_days} days, paler part = still open`}>
          <Bars rows={s.by_category || []} />
        </Card>
        <Card title="By priority" sub={`Last ${s.window_days} days`}>
          <ul className="space-y-2.5">
            {priorities.map((p) => (
              <li key={p.label} className="flex items-center gap-3 text-sm">
                <span className={'h-2.5 w-2.5 rounded-full ' + (priorityTone[p.label] || 'bg-gray-300')} />
                <span className="flex-1 font-medium text-gray-800">{p.label}</span>
                <span className="tabular-nums text-gray-600">{p.total}</span>
                <span className="w-16 text-right text-xs text-gray-400">{p.open ? `${p.open} open` : ''}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Open ticket age" sub="How long the current queue has been waiting">
          <Bars rows={(s.ageing || []).map((a) => ({ label: a.label, total: a.n }))} tone="bg-amber-400" />
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Who is carrying the work" sub={`Open now and closed in the last ${s.window_days} days`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                <th className="pb-2 font-semibold">Person</th>
                <th className="pb-2 text-right font-semibold">Open</th>
                <th className="pb-2 text-right font-semibold">Closed</th>
                <th className="pb-2 text-right font-semibold">Typical fix</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(s.by_assignee || []).map((p) => (
                <tr key={p.name}>
                  <td className="py-2">
                    <span className="flex items-center gap-2">
                      <Avatar name={p.name} size="h-6 w-6 text-[10px]" tone={p.name === 'Unassigned' ? 'bg-gray-100 text-gray-400' : 'bg-brand-light text-brand'} />
                      <span className="font-medium text-gray-800">{p.name}</span>
                    </span>
                  </td>
                  <td className="py-2 text-right tabular-nums text-gray-700">{p.open || '—'}</td>
                  <td className="py-2 text-right tabular-nums text-gray-700">{p.closed || '—'}</td>
                  <td className="py-2 text-right tabular-nums text-gray-500">{fmtDuration(p.resolution_median_h)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card title="When tickets come in" sub={`Weekday by hour, last ${s.window_days} days`}>
          <Heatmap heat={s.heat} />
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Service levels" sub={`Last ${s.window_days} days, medians`}>
          <dl className="grid grid-cols-2 gap-4">
            <div><dt className="text-xs text-gray-500">First response</dt><dd className="text-xl font-bold text-gray-900">{fmtDuration(t.first_response_median_h)}</dd></div>
            <div><dt className="text-xs text-gray-500">Time to resolve</dt><dd className="text-xl font-bold text-gray-900">{fmtDuration(t.resolution_median_h)}</dd></div>
            <div><dt className="text-xs text-gray-500">Answered within 4h</dt><dd className="text-xl font-bold text-gray-900">{t.responded_within_4h_pct ?? '—'}{t.responded_within_4h_pct != null ? '%' : ''}</dd></div>
            <div><dt className="text-xs text-gray-500">Sorted within a day</dt><dd className="text-xl font-bold text-gray-900">{t.resolved_within_24h_pct ?? '—'}{t.resolved_within_24h_pct != null ? '%' : ''}</dd></div>
            <div className="col-span-2"><dt className="text-xs text-gray-500">Open tickets nobody has replied to yet</dt>
              <dd className={'text-xl font-bold ' + (t.awaiting_first_response ? 'text-rose-600' : 'text-emerald-600')}>{n(t.awaiting_first_response)}</dd></div>
          </dl>
        </Card>
        <Card title="Who raises the most" sub={`Top requesters, last ${s.window_days} days`}>
          <Bars rows={(s.requesters || []).map((r) => ({ label: r.name, total: r.total }))} tone="bg-blue-400" />
        </Card>
      </div>
    </div>
  );
}

// ---- Queue ------------------------------------------------------------------

// The queue, for the IT team. Defaults to open tickets only: closed ones are
// history and would bury the work within a month.
function Queue({ meta, onOpen, s }) {
  const [view, setView] = useState('open');
  const [filters, setFilters] = useState({ category: '', assignee: '', q: '' });
  const { data: assignable } = useAssignableUsers(true);
  const params = {
    ...filters,
    needs_reply: view === 'needs_reply' ? '1' : '',
    open: view === 'open' || view === 'needs_reply' ? '1' : '',
    status: ['new', 'in_progress', 'waiting_on_user', 'resolved', 'closed'].includes(view) ? view : '',
    assignee: view === 'unassigned' ? 'unassigned' : filters.assignee,
  };
  if (view === 'unassigned') params.open = '1';
  const { data: tickets } = useTicketQueue(params);
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <StatusPills s={s} value={view} onChange={setView} />

      <div className="mb-3 flex flex-wrap gap-2">
        <div className="relative min-w-[14rem] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input value={filters.q} onChange={set('q')} placeholder="Search reference, subject or person"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm" />
        </div>
        <select value={filters.category} onChange={set('category')} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
          <option value="">All categories</option>
          {Object.entries(meta?.categories || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {view !== 'unassigned' && (
          <select value={filters.assignee} onChange={set('assignee')} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
            <option value="">Anyone</option>
            <option value="unassigned">Unassigned</option>
            {(assignable || []).map((p) => <option key={p.entraId} value={p.entraId}>{p.name}</option>)}
          </select>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <span>{tickets ? `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}` : 'Tickets'}</span>
          <span className="hidden md:block">Last update · Owner</span>
        </div>
        <TicketList tickets={tickets} meta={meta} onOpen={onOpen} empty="Nothing here." />
      </div>
    </>
  );
}

export default function ItSupport() {
  const { data: meta } = useTicketMeta();
  const { data: mine } = useMyTickets();
  const [tab, setTab] = useState('mine');
  const [raising, setRaising] = useState(false);
  // ?ticket=ID opens that ticket straight away -- the inbox's 'Open the ticket
  // to reply' button lands here.
  const [params, setParams] = useSearchParams();
  const [openId, setOpenIdState] = useState(() => Number(params.get('ticket')) || null);
  const setOpenId = (v) => {
    setOpenIdState(v);
    if (!v && params.get('ticket')) { params.delete('ticket'); setParams(params, { replace: true }); }
  };
  const isAgent = Boolean(meta?.isAgent);
  const { data: stats } = useTicketStats(isAgent);

  // Someone who works the queue almost always wants the queue, not their own
  // two tickets. Only switches once, so a deliberate click is not overridden.
  const jumped = useRef(false);
  useEffect(() => {
    if (isAgent && !jumped.current) { jumped.current = true; setTab('queue'); }
  }, [isAgent]);

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-light">
            <LifebuoyIcon className="h-6 w-6 text-brand" />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">IT Support</h1>
            <p className="text-sm text-gray-500">
              Problems with the platform or the app, access requests, and anything else IT.
            </p>
          </div>
        </div>
        <button type="button" onClick={() => setRaising(true)}
          className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90">
          <PlusIcon className="h-4 w-4" /> Raise a ticket
        </button>
      </div>

      {isAgent && <KpiStrip s={stats} />}

      {isAgent && (
        <div className="mb-5 flex gap-2 border-b border-gray-200">
          {[['queue', 'Queue', QueueListIcon], ['insights', 'Insights', ChartBarIcon], ['mine', 'My tickets', UserIcon]].map(([k, label, Icon]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={'-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-semibold ' + (tab === k
                ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </div>
      )}

      {tab === 'queue' && isAgent ? (
        <Queue meta={meta} onOpen={setOpenId} s={stats} />
      ) : tab === 'insights' && isAgent ? (
        <Insights s={stats} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <TicketList tickets={mine} meta={meta} onOpen={setOpenId}
            empty="You have not raised any tickets." />
        </div>
      )}

      {raising && <RaiseForm onClose={() => setRaising(false)} />}
      {openId && (
        <TicketDetail id={openId} isAgent={isAgent} meta={meta} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}
